import { takeEvery, takeLatest } from 'redux-saga/effects';
import { getDynamicSettings, getFileUrl } from 'src/features/globalSlice';

import {
  changePassword,
  changeSettings,
  changeTheme,
  checkUser,
  getSupportInfo,
  login,
  sendNewPassword,
  switchManufacturer,
  syncUser,
} from '../actions/authActions';
import {
  addCompany,
  editCompany,
  getCompanyDetails,
  getCompanyList,
  removeCompany,
} from '../actions/companyActions';
import {
  changeParameters,
  getDeviceDetails,
  getParameterDetails,
} from '../actions/deviceActions';
import {
  addManufacturerAccount,
  changeManufacturerAccountStatus,
  editManufacturerAccount,
  getManufacturerAccountDetails,
  getManufacturerAccountList,
  removeManufacturerAccount,
} from '../actions/manufacturerAccountActions';
import {
  addProduct,
  editProduct,
  getErrorsTable,
  getProduct,
  getProductList,
  removeProduct,
} from '../actions/productActions';
import {
  addServiceAccount,
  bulkEditAutoAssign,
  changeServiceAccountStatus,
  editServiceAccountDetails,
  getAutoAssignServiceAccountList,
  getServiceAccountDetails,
  getServiceAccountList,
  removeServiceAccount,
} from '../actions/serviceAccountActions';
import {
  accessTicketPIN,
  addTicketNote,
  assignTicket,
  createTicket,
  getPriorityTickets,
  getTicketDetails,
  getTicketList,
  getTicketNotes,
  getTicketStatistics,
  reassignTicket,
  resolveTicket,
} from '../actions/ticketActions';
import {
  AuthChangePasswordSaga,
  AuthChangeSettingsSaga,
  AuthChangeThemeSaga,
  AuthCheckUserSaga,
  AuthGetSupportInfoSaga,
  AuthLoginSaga,
  AuthSendNewPasswordSaga,
  AuthSwitchManufacturerSaga,
  AuthSyncUserSaga,
} from './authSaga';
import {
  CompanyAddNewSaga,
  CompanyEditSaga,
  CompanyGetDetailsSaga,
  CompanyGetListSaga,
  CompanyRemoveSaga,
} from './companySaga';
import {
  DeviceChangeParametersSaga,
  DeviceGetDetailsSaga,
  DeviceGetParameterDetailsSaga,
} from './deviceSaga';
import {
  GlobalGetDynamicSettingsSaga,
  GlobalGetFileURLSaga,
} from './globalSaga';
import {
  ManufacturerAccountGetDetailsSaga,
  ManufacturerAddAccountSaga,
  ManufacturerChangeAccountStatusSaga,
  ManufacturerEditAccountSaga,
  ManufacturerGetListSaga,
  ManufacturerRemoveSaga,
} from './manufacturerAccountSaga';
import {
  ProductAddProductSaga,
  ProductEditProductSaga,
  ProductGetErrorsSaga,
  ProductGetListSaga,
  ProductGetProductSaga,
  ProductRemoveProductSaga,
} from './productSaga';
import {
  ServiceAccountBulkEditAutoAssignSaga,
  ServiceAccountGetAutoAssignListSaga,
  ServiceAccountGetDetailsSaga,
  ServiceAccountGetListSaga,
  ServiceAddAccountSaga,
  ServiceChangeAccountStatusSaga,
  ServiceEditAccountSaga,
  ServiceRemoveSaga,
} from './serviceAccountSaga';
import {
  TicketAccessTicketPINSaga,
  TicketAddTicketNoteSaga,
  TicketAssignTicketSaga,
  TicketCreateTicketSaga,
  TicketGetPriorityTicketsSaga,
  TicketGetStatisticsSaga,
  TicketGetTicketDetailsSaga,
  TicketGetTicketListSaga,
  TicketGetTicketNotesSaga,
  TicketReassignTicketSaga,
  TicketResolveTicketSaga,
} from './ticketSaga';

export interface ApiResponse<T> {
  data: T;
}

export function* watchAuth(): Generator {
  yield takeLatest(login.type, AuthLoginSaga);
  yield takeLatest(checkUser.type, AuthCheckUserSaga);
  yield takeLatest(syncUser.type, AuthSyncUserSaga);
  yield takeLatest(changePassword.type, AuthChangePasswordSaga);
  yield takeLatest(changeSettings.type, AuthChangeSettingsSaga);
  yield takeLatest(switchManufacturer.type, AuthSwitchManufacturerSaga);
  yield takeLatest(getSupportInfo.type, AuthGetSupportInfoSaga);
  yield takeLatest(sendNewPassword.type, AuthSendNewPasswordSaga);
  yield takeLatest(changeTheme.type, AuthChangeThemeSaga);
}

export function* watchTicket(): Generator {
  yield takeLatest(getTicketList.type, TicketGetTicketListSaga);
  yield takeLatest(getPriorityTickets.type, TicketGetPriorityTicketsSaga);
  yield takeLatest(getTicketDetails.type, TicketGetTicketDetailsSaga);
  yield takeLatest(createTicket.type, TicketCreateTicketSaga);
  yield takeLatest(accessTicketPIN.type, TicketAccessTicketPINSaga);
  yield takeLatest(getTicketNotes.type, TicketGetTicketNotesSaga);
  yield takeLatest(addTicketNote.type, TicketAddTicketNoteSaga);
  yield takeLatest(resolveTicket.type, TicketResolveTicketSaga);
  yield takeLatest(assignTicket.type, TicketAssignTicketSaga);
  yield takeLatest(reassignTicket.type, TicketReassignTicketSaga);
  yield takeEvery(getTicketStatistics.type, TicketGetStatisticsSaga);
}

export function* watchDevice(): Generator {
  yield takeLatest(getDeviceDetails.type, DeviceGetDetailsSaga);
  yield takeLatest(getParameterDetails.type, DeviceGetParameterDetailsSaga);
  yield takeLatest(changeParameters.type, DeviceChangeParametersSaga);
}

export function* watchProduct(): Generator {
  yield takeLatest(addProduct.type, ProductAddProductSaga);
  yield takeLatest(getProduct.type, ProductGetProductSaga);
  yield takeLatest(editProduct.type, ProductEditProductSaga);
  yield takeLatest(removeProduct.type, ProductRemoveProductSaga);
  yield takeLatest(getProductList.type, ProductGetListSaga);
  yield takeLatest(getErrorsTable.type, ProductGetErrorsSaga);
}

export function* watchServiceAccount(): Generator {
  yield takeLatest(getServiceAccountList.type, ServiceAccountGetListSaga);
  yield takeLatest(getServiceAccountDetails.type, ServiceAccountGetDetailsSaga);
  yield takeEvery(
    changeServiceAccountStatus.type,
    ServiceChangeAccountStatusSaga
  );
  yield takeLatest(editServiceAccountDetails.type, ServiceEditAccountSaga);
  yield takeLatest(addServiceAccount.type, ServiceAddAccountSaga);
  yield takeLatest(removeServiceAccount.type, ServiceRemoveSaga);
  yield takeLatest(
    getAutoAssignServiceAccountList.type,
    ServiceAccountGetAutoAssignListSaga
  );
  yield takeLatest(
    bulkEditAutoAssign.type,
    ServiceAccountBulkEditAutoAssignSaga
  );
}

export function* watchCompany(): Generator {
  yield takeLatest(addCompany.type, CompanyAddNewSaga);
  yield takeLatest(getCompanyDetails.type, CompanyGetDetailsSaga);
  yield takeLatest(editCompany.type, CompanyEditSaga);
  yield takeLatest(getCompanyList.type, CompanyGetListSaga);
  yield takeLatest(removeCompany.type, CompanyRemoveSaga);
}

export function* watchGlobal(): Generator {
  yield takeEvery(getFileUrl.type, GlobalGetFileURLSaga);
  yield takeEvery(getDynamicSettings.type, GlobalGetDynamicSettingsSaga);
}

export function* watchManufacturerAccount(): Generator {
  yield takeLatest(getManufacturerAccountList.type, ManufacturerGetListSaga);
  yield takeLatest(addManufacturerAccount.type, ManufacturerAddAccountSaga);
  yield takeLatest(editManufacturerAccount.type, ManufacturerEditAccountSaga);
  yield takeLatest(
    getManufacturerAccountDetails.type,
    ManufacturerAccountGetDetailsSaga
  );
  yield takeEvery(
    changeManufacturerAccountStatus.type,
    ManufacturerChangeAccountStatusSaga
  );
  yield takeLatest(removeManufacturerAccount.type, ManufacturerRemoveSaga);
}
